import React, { useMemo } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';

import { SkeletonProps } from './Skeleton.types';
import { Flex, Table, TableColumn } from 'src/packages/core';
import { DarkTheme, LightTheme, typographyScale } from 'src/packages/theme';

const animation = keyframes`
  0% {
    left: -500px;
  }
  100% {
    left: 100%;
  }
`;

const SkeletonElement = styled.div<{
  light: boolean;
}>`
  line-height: 1;
  display: block;
  overflow: hidden;
  position: relative;
  direction: ltr;

  &::before {
    content: '';
    position: absolute;
    height: 100%;
    width: 500px;
    top: 0;
    left: -500px;
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, ${(props) => (props.light ? 0.6 : 0.4)}),
      rgba(255, 255, 255, 0)
    );
    animation: ${animation} 1.5s ease-in-out infinite;
  }
`;

const Skeleton: React.FC<SkeletonProps> = (props: SkeletonProps): React.ReactElement => {
  const {
    width = '100%',
    height = '16px',
    backgroundColor,
    circle = false,
    table = false,
    columns = undefined,
    rows = 1,
    textVariant = undefined,
    block = true,
    borderRadius = '4px',
    theme,
    style = {},
    withBorder = false,
  } = props;

  const contextTheme = useTheme();
  const isMobile = useMediaQuery(contextTheme.mediaQueries.md);

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const defaultBackgroundColor = defaultTheme.colors.background.secondary.BG4;

  if (textVariant) {
    const getContainerHeight = () => {
      return isMobile
        ? typographyScale.mobile[textVariant].lineHeight
        : typographyScale.desktop[textVariant].lineHeight;
    };

    const getLineHeight = () => {
      return isMobile
        ? typographyScale.mobile[textVariant].fontSize
        : typographyScale.desktop[textVariant].fontSize;
    };

    return (
      <Flex flexDirection={'column'} gap={1} width={width} style={style}>
        {[...Array(rows).keys()].map((i) => (
          <Flex key={i} height={getContainerHeight()} alignItems={'center'}>
            <SkeletonElement
              light={theme === 'light'}
              style={{
                width: '100%',
                height: getLineHeight(),
                display: 'block',
                backgroundColor: backgroundColor ?? defaultBackgroundColor,
                borderRadius,
                border: withBorder ? `1px solid ${defaultTheme.colors.border.middle}` : 'unset',
              }}
            />
          </Flex>
        ))}
      </Flex>
    );
  }

  if (table && columns) {
    const getSkeleton = (elem: 'label' | 'rowData') => {
      const typography = elem === 'label' ? 'p3' : 'p2';
      return (
        <SkeletonElement
          light={theme === 'light'}
          style={{
            width,
            height: isMobile
              ? typographyScale.mobile[typography].fontSize
              : typographyScale.desktop[typography].fontSize,
            backgroundColor: backgroundColor ?? defaultBackgroundColor,
            borderRadius,
            border: withBorder ? `1px solid ${defaultTheme.colors.border.middle}` : 'unset',
            display: 'block',
            ...style,
          }}
        />
      );
    };

    const generateTableColumns = () => {
      // eslint-disable-next-line
      let cols: TableColumn[] = [];

      for (let i = 0; i < columns; i++) {
        cols.push({
          label: getSkeleton('label'),
          cellRenderer: () => getSkeleton('rowData'),
        });
      }

      return cols;
    };

    const generateData = () => {
      // eslint-disable-next-line
      let data: any[] = [];

      [...Array(rows).keys()].forEach(() => {
        const obj = [...Array(columns).keys()].reduce((acc: Record<any, any>, val) => {
          acc[val] = val;
          return acc;
        }, {});

        data.push(obj);
      });

      return data;
    };

    return (
      <Table
        theme={theme}
        data={generateData()}
        columns={generateTableColumns()}
        highlightRowsOnHover={false}
        borderRadius={borderRadius}
        p={0}
      />
    );
  }

  return (
    <SkeletonElement
      light={theme === 'light'}
      style={{
        width,
        height,
        backgroundColor: backgroundColor ?? defaultBackgroundColor,
        borderRadius: circle ? '50%' : borderRadius,
        border: withBorder ? `1px solid ${defaultTheme.colors.border.middle}` : 'unset',
        display: block ? 'block' : 'inline-block',
        ...style,
      }}
    />
  );
};

export default Skeleton;
